import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";

export const useRiderGPS = (orderId, userId) => {
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [position, setPosition] = useState(null);
  const session = useRef(null);
  const watch = useRef(null);
  const generation = useRef(0);
  const lastSent = useRef(0);
  const sending = useRef(false);
  const starting = useRef(false);

  const halt = useCallback(() => {
    generation.current++;
    if (watch.current !== null) navigator.geolocation?.clearWatch(watch.current);
    watch.current = null;
    setRunning(false); setPosition(null); setBusy(false);
    const active = session.current;
    session.current = null;
    if (!active) return Promise.resolve();
    return fetch(`${API}/rider/orders/${active.orderId}/stop`, { method: "POST", credentials: "include", keepalive: true,
      headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: active.id }) });
  }, []);

  const stop = useCallback(async () => {
    try { const res = await halt(); setMessage(res && !res.ok ? "GPS stopped on this device. Server could not confirm; the last fix will expire within two minutes." : "Location sharing stopped."); }
    catch { setMessage("GPS stopped on this device. Offline readings expire from customer view within two minutes."); }
  }, [halt]);

  useEffect(() => {
    const hidden = () => { if (document.visibilityState !== "visible") { halt().catch(() => {}); setMessage("Sharing paused because the page was hidden. Start again when ready."); } };
    const offline = () => { halt().catch(() => {}); setMessage("Sharing stopped while offline. Reconnect and start again."); };
    window.addEventListener("pagehide", hidden); document.addEventListener("visibilitychange", hidden); window.addEventListener("offline", offline);
    return () => { halt().catch(() => {}); window.removeEventListener("pagehide", hidden); document.removeEventListener("visibilitychange", hidden); window.removeEventListener("offline", offline); };
  }, [orderId, userId, halt]);

  const start = async () => {
    if (!orderId || !userId || starting.current || session.current) return;
    if (!window.isSecureContext || !navigator.geolocation) { setMessage("GPS requires HTTPS and a browser with location access."); return; }
    const current = ++generation.current;
    starting.current = true; setBusy(true); setMessage("Waiting for location permission…"); lastSent.current = 0;
    const publish = async (fix) => {
      if (current !== generation.current || !session.current || sending.current || Date.now() - lastSent.current < 5000) return;
      const payload = { latitude: fix.coords.latitude, longitude: fix.coords.longitude, accuracy_m: fix.coords.accuracy, captured_at: new Date(fix.timestamp).toISOString() };
      sending.current = true;
      try {
        await axios.post(`${API}/rider/orders/${orderId}/position`, { ...payload, session_id: session.current.id });
        if (current !== generation.current) return;
        lastSent.current = Date.now(); setPosition(payload); setMessage("GPS sharing is active.");
      } catch (err) { if (current === generation.current) { halt().catch(() => {}); setMessage(err.response?.data?.detail || "GPS update failed. Start again when your connection is ready."); } }
      finally { sending.current = false; }
    };
    try {
      const first = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 }));
      if (current !== generation.current) return;
      const { data } = await axios.post(`${API}/rider/orders/${orderId}/start`);
      session.current = { id: data.session_id, orderId };
      if (current !== generation.current) { halt().catch(() => {}); return; }
      setRunning(true);
      await publish(first);
      if (current !== generation.current) return;
      watch.current = navigator.geolocation.watchPosition(publish, (err) => {
        halt().catch(() => {}); setMessage(err.code === 1 ? "Location permission denied. Allow location access to share GPS." : "GPS signal unavailable. Move to a better signal and start again.");
      }, { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 });
    } catch (err) {
      if (current === generation.current) { halt().catch(() => {}); setMessage(err.code === 1 ? "Location permission denied. Allow location access to share GPS." : err.response?.data?.detail || "Could not start GPS. Check location permission and your connection."); }
    } finally { starting.current = false; if (current === generation.current) setBusy(false); }
  };
  return { start, stop, busy, running, message, position };
};